import { useLocation } from 'react-router';

import { REDIRECT_QUERY_KEY } from '@jane/bloom/utils/queryParams/queryParams';

interface GetRedirectRouteParams {
  pathname: string;
  search?: string;
  to: string;
}

function getRedirectRoute({ pathname, search, to }: GetRedirectRouteParams) {
  return `${to}?${REDIRECT_QUERY_KEY}=${pathname}${encodeURIComponent(search ?? '')}`;
}

type GetRouteWithRedirect = (to: string) => string;

/**
 * Custom hook that returns a function to calculate the redirect route.
 * @param {boolean} withoutRedirectParam - If true, does not add the redirect query param to the route
 * @returns {GetRouteWithRedirect} A function that returns the redirect route
 */
export function useRouteWithRedirect(
  withoutRedirectParam: boolean = false
): GetRouteWithRedirect {
  const location = useLocation();

  const getRouteWithRedirect: GetRouteWithRedirect = (to: string) => {
    return withoutRedirectParam
      ? to
      : getRedirectRoute({
          pathname: location.pathname,
          search: location.search,
          to,
        });
  };

  return getRouteWithRedirect;
}

interface RouteWithRedirectParams {
  basename: string;
  redirectTo: string;
  to: string;
}

/**
 * Calculates the redirect route. Compatible with actions and loaders.
 * @returns {string} the redirect route
 */
export const routeWithRedirect = ({
  basename,
  redirectTo,
  to,
}: RouteWithRedirectParams): string => {
  return `${basename}/${to}?${REDIRECT_QUERY_KEY}=${encodeURIComponent(`${basename}/${redirectTo}`)}`;
};
